import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { bookingPriceAud } from '@/domain/pricing';
import { COMPLETED_PIECE_STATUSES, sessionGrossAud } from '@/domain/reportingEngine';
import { salesByBookingId } from '@/domain/sessionTakings';
import { aud, simDateShort } from '@/utils/format';
import { piecesForCustomer } from '@/utils/pieceHelpers';
import PhotoGallery, { type PhotoGalleryItem } from '@/components/PhotoGallery';
import type { Booking, CustomerRecord, Piece } from '@/domain/types';

/** Bookings that never happened and shouldn't read as history. */
const HIDDEN_STATUSES = new Set<Booking['status']>(['cancelled', 'superseded']);

/** Statuses that mean the client actually sat. */
function hasSat(b: Booking): boolean {
  return b.status === 'completed' || b.checkedOutAt != null;
}

/** A progress photo, as the rail shows it. */
type Progress = NonNullable<Piece['progressPhotos']>[number];

interface Visit {
  booking: Booking;
  /** 1-based position of this sitting within its piece. */
  sessionNo: number | null;
  /** What the sitting took (past) or is expected to take (upcoming). */
  amountAud: number;
  past: boolean;
  /** They booked in and didn't show — listed, but no money claimed. */
  noShow: boolean;
  /** Progress captured at this sitting. */
  photos: Progress[];
}

interface Group {
  key: string;
  title: string;
  piece?: Piece;
  visits: Visit[];
  /** Sum of what past sittings actually took. */
  takenAud: number;
  /** Photos on the piece that name no sitting we can show — never hidden. */
  loosePhotos: Progress[];
}

/**
 * Session number within a piece: position in `bookingsLinked` ordered by date.
 * Same derivation the customer record uses, so the two never disagree.
 */
function sessionNoFor(
  piece: Piece | undefined,
  booking: Booking,
  bookings: Record<string, Booking>,
): number | null {
  if (!piece) return null;
  const linked = piece.bookingsLinked
    .map((id) => bookings[id])
    .filter((b): b is Booking => Boolean(b))
    .sort((x, y) => (x.start < y.start ? -1 : 1));
  const idx = linked.findIndex((x) => x.id === booking.id);
  return idx >= 0 ? idx + 1 : (piece.sessionsCompleted ?? linked.length) + 1;
}

/**
 * A progress photo as the gallery shows it. References carry a burned-in tag so
 * the artist can tell "what I was working from" apart from "what I did" at
 * thumbnail size; progress is the default and stays unbadged.
 */
function galleryItem(p: Progress): PhotoGalleryItem {
  return {
    url: p.url,
    caption: p.caption,
    capturedAt: p.capturedAt,
    ...(p.kind === 'reference' ? { tag: 'Ref', tagTone: 'azure' as const } : {}),
  };
}

/** A piece label that always reads, even when the piece was never titled. */
function pieceTitle(piece: Piece): string {
  return piece.title ?? `${piece.designType.replace('-', ' ')} on ${piece.placement}`;
}

/**
 * Dates drop the year (`d MMM`) which reads wrong on work from previous years —
 * a 2024 sitting rendering as a bare "9 Nov" looks like it was this year. Add the
 * year only when it isn't the current one, so recent rows stay short.
 */
function visitDate(iso: string, nowIso: string): string {
  const short = simDateShort(iso);
  const year = iso.slice(0, 4);
  return year === nowIso.slice(0, 4) ? short : `${short} ${year.slice(2)}`;
}

/** The span a finished piece ran over: first sitting to last. */
function finishedSpan(group: Group, nowIso: string): string | null {
  const past = group.visits.filter((v) => v.past);
  if (past.length === 0) return null;
  const first = past[past.length - 1]!.booking.start;
  const last = group.piece?.lastSessionDate ?? past[0]!.booking.start;
  const from = visitDate(first, nowIso);
  const to = visitDate(last, nowIso);
  return from === to ? from : `${from} – ${to}`;
}

/**
 * The client's history, grouped by piece — what they're carrying, when they've
 * been in and what each sitting took. Read-only: it's the artist's context for
 * the chair, and replaces sending them into the front-desk customer record.
 */
export default function ClientHistoryPanel({
  customer,
  currentPieceId,
  currentBookingId,
}: {
  customer: CustomerRecord;
  currentPieceId?: string;
  currentBookingId?: string;
}) {
  const bookings = useBrainStore((s) => s.bookings);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const salePayments = useBrainStore((s) => s.salePayments);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const groups = useMemo<Group[]>(() => {
    // Sales keyed by booking so the charge helper reads the same number payouts
    // and reporting do.
    const salesByBooking = salesByBookingId(salePayments);

    const mine = customer.bookings
      .map((id) => bookings[id])
      .filter((b): b is Booking => b != null && !HIDDEN_STATUSES.has(b.status))
      .sort((x, y) => (x.start < y.start ? 1 : -1));

    const toVisit = (b: Booking): Visit => {
      const past = hasSat(b);
      const noShow = b.status === 'no-show';
      const piece = b.pieceId ? pieces[b.pieceId] : undefined;
      return {
        booking: b,
        sessionNo: sessionNoFor(piece, b, bookings),
        amountAud: noShow
          ? 0
          : past
            ? sessionGrossAud(b, salesByBooking, artists)
            : bookingPriceAud(b, artists[b.artistId]),
        past,
        noShow,
        // ontheday-2: a one-off session (no piece) carries its photos itself.
        photos: piece ? (piece.progressPhotos ?? []).filter((p) => p.bookingId === b.id) : (b.progressPhotos ?? []),
      };
    };

    const byKey = new Map<string, Group>();
    const groupFor = (key: string, title: string, piece?: Piece): Group => {
      const existing = byKey.get(key);
      if (existing) return existing;
      const created: Group = { key, title, piece, visits: [], takenAud: 0, loosePhotos: [] };
      byKey.set(key, created);
      return created;
    };

    // Pieces the customer owns come first, so a piece with no sittings yet still
    // gets a group.
    for (const p of piecesForCustomer(pieces, customer)) groupFor(p.id, pieceTitle(p), p);

    for (const b of mine) {
      const piece = b.pieceId ? pieces[b.pieceId] : undefined;
      const group = piece
        ? groupFor(piece.id, pieceTitle(piece), piece)
        : groupFor('__oneoff', 'One-off sessions');
      const visit = toVisit(b);
      group.visits.push(visit);
      if (visit.past) group.takenAud += visit.amountAud;
    }

    // Any photo whose sitting isn't on the rail still has to show up somewhere.
    for (const group of byKey.values()) {
      const shown = new Set(group.visits.flatMap((v) => v.photos.map((p) => p.id)));
      group.loosePhotos = (group.piece?.progressPhotos ?? []).filter((p) => !shown.has(p.id));
    }

    // Most recently active piece first; the open booking's piece always leads.
    const activity = (g: Group) =>
      g.visits.length > 0 ? g.visits[0]!.booking.start : (g.piece?.lastSessionDate ?? '');

    return [...byKey.values()]
      .filter((g) => g.visits.length > 0 || g.piece != null)
      .sort((a, b) => {
        if (a.piece?.id === currentPieceId) return -1;
        if (b.piece?.id === currentPieceId) return 1;
        if (a.key === '__oneoff') return 1;
        if (b.key === '__oneoff') return -1;
        return activity(a) < activity(b) ? 1 : -1;
      });
  }, [customer, bookings, pieces, artists, salePayments, currentPieceId]);

  const [openKeys, setOpenKeys] = useState<Set<string>>(
    () => new Set(currentPieceId ? [currentPieceId] : groups.slice(0, 1).map((g) => g.key)),
  );
  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sittings = groups.reduce((n, g) => n + g.visits.filter((v) => v.past).length, 0);

  if (groups.length === 0) {
    return (
      <div className="panel p-4" data-testid="client-history">
        <p className="label-small-caps text-gold-bright">Client history</p>
        <p className="panel-dark mt-3 p-4 font-serif text-cream-soft" data-testid="client-history-empty">
          Nothing on file yet — this is {customer.firstName}&apos;s first time in.
        </p>
      </div>
    );
  }

  return (
    <div className="panel p-4" data-testid="client-history">
      <p className="label-small-caps text-gold-bright">Client history</p>

      <div className="mt-3 flex items-baseline justify-between gap-2">
        <div>
          <p className="font-serif text-[13px] text-cream-soft">
            {groups.filter((g) => g.piece).length} piece
            {groups.filter((g) => g.piece).length === 1 ? '' : 's'} · {sittings} sitting
            {sittings === 1 ? '' : 's'}
          </p>
          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
            Lifetime spend
          </p>
        </div>
        <span className="font-cinzel text-2xl leading-none text-gold-bright" data-testid="client-history-lifetime">
          {aud(customer.lifetimeSpendAud)}
        </span>
      </div>

      <div className="mt-4">
        {groups.map((group) => {
          const open = openKeys.has(group.key);
          const finished =
            group.piece != null && COMPLETED_PIECE_STATUSES.has(group.piece.status);
          const span = finished ? finishedSpan(group, clockIso) : null;
          const upcoming = group.visits.filter((v) => !v.past);
          const pastVisits = group.visits.filter((v) => v.past);

          return (
            <div key={group.key} className="border-t border-gold-dim/25 last:border-b">
              <button
                type="button"
                onClick={() => toggle(group.key)}
                aria-expanded={open}
                className="flex w-full items-center gap-2 py-2.5 text-left"
                data-testid={`client-history-group-${group.key}`}
              >
                {open ? (
                  <ChevronDown size={13} className="shrink-0 text-gold-bright" />
                ) : (
                  <ChevronRight size={13} className="shrink-0 text-cream-faint" />
                )}
                <span className="font-serif text-sm text-cream">{group.title}</span>
                {finished ? (
                  <span className="pill-mint shrink-0 px-1.5 py-0 text-[9px]">done</span>
                ) : null}
                <span
                  className={clsx(
                    'ml-auto shrink-0 font-mono text-[12.5px] tabular-nums',
                    group.takenAud > 0 ? 'text-gold-bright' : 'text-cream-faint',
                  )}
                >
                  {group.takenAud > 0 ? aud(group.takenAud) : 'no sittings yet'}
                </span>
              </button>

              {open ? (
                <div className="flex flex-col gap-2.5 pb-3">
                  {group.piece ? (
                    <p
                      className="font-inter text-[10.5px] uppercase tracking-widest text-cream-faint"
                      data-testid={`client-history-sub-${group.key}`}
                    >
                      {span ? (
                        <>
                          <span className="font-bold text-mint">Finished · {span}</span>
                          {' · '}
                          {pastVisits.length} sitting{pastVisits.length === 1 ? '' : 's'}
                          {' · '}
                          {group.piece.hoursCompleted}h
                        </>
                      ) : (
                        <>
                          {group.piece.status.replace(/-/g, ' ')} · {group.piece.hoursCompleted}h of{' '}
                          {group.piece.estimatedTotalHours}h
                          {group.piece.estimatedTotalSessions
                            ? ` · ${group.piece.sessionsCompleted ?? pastVisits.length} of ${group.piece.estimatedTotalSessions} sessions`
                            : ''}
                        </>
                      )}
                    </p>
                  ) : null}

                  {pastVisits.length > 0 ? (
                    <ul className="divide-y divide-gold-dim/15">
                      {pastVisits.map((v) => (
                        <VisitRow
                          key={v.booking.id}
                          visit={v}
                          artistName={
                            artists[v.booking.artistId]?.displayName ?? v.booking.artistId
                          }
                          nowIso={clockIso}
                          isCurrent={v.booking.id === currentBookingId}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="font-serif text-[12px] italic text-cream-faint">
                      No sittings recorded on this piece yet.
                    </p>
                  )}

                  {group.loosePhotos.length > 0 ? (
                    <PhotoGallery
                      photos={group.loosePhotos.map(galleryItem)}
                      label="Not tied to a sitting"
                      size="thumb"
                      density="strip"
                      maxVisible={6}
                      testid={`client-history-loose-${group.key}`}
                    />
                  ) : null}

                  {upcoming.length > 0 ? (
                    <div className="border-t border-dashed border-gold-dim/40 pt-2">
                      <p className="font-inter text-[10.5px] uppercase tracking-widest text-cream-faint mb-1">
                        Booked next
                      </p>
                      <ul className="divide-y divide-gold-dim/15">
                        {upcoming.map((v) => (
                          <VisitRow
                            key={v.booking.id}
                            visit={v}
                            artistName={
                              artists[v.booking.artistId]?.displayName ?? v.booking.artistId
                            }
                            nowIso={clockIso}
                            isCurrent={v.booking.id === currentBookingId}
                          />
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** One sitting: when, which session, who, and what it took. */
function VisitRow({
  visit,
  artistName,
  nowIso,
  isCurrent,
}: {
  visit: Visit;
  artistName: string;
  nowIso: string;
  isCurrent: boolean;
}) {
  const { booking, sessionNo, amountAud, past, noShow, photos } = visit;
  const inChair = booking.status === 'in-chair' || booking.status === 'checked-in';

  return (
    <li
      className={clsx(
        'grid grid-cols-[58px_1fr_auto] items-center gap-x-2.5 gap-y-1.5 px-1.5 py-1.5 text-xs',
        isCurrent && 'rounded-[5px] bg-gold-bright/10',
      )}
      data-testid={`client-history-visit-${booking.id}`}
    >
      <span className="font-mono text-[10px] uppercase tracking-wider text-cream-faint">
        {visitDate(booking.start, nowIso)}
      </span>
      <span
        className={clsx(
          'font-serif truncate',
          noShow ? 'text-cream-faint' : 'text-cream-soft',
        )}
      >
        {sessionNo != null ? `s${sessionNo} · ` : ''}
        {artistName}
        {inChair ? <span className="pill-mint ml-1.5 px-1.5 py-0 text-[9px]">in chair now</span> : null}
        {noShow ? <span className="pill-blood ml-1.5 px-1.5 py-0 text-[9px]">no show</span> : null}
      </span>
      <span
        className={clsx(
          'text-right font-mono tabular-nums',
          past && !noShow ? 'text-cream' : 'text-cream-faint',
        )}
      >
        {inChair || noShow || amountAud === 0 ? (
          '—'
        ) : (
          <>
            {aud(amountAud)}
            {!past ? (
              <span className="ml-1 font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                expected
              </span>
            ) : null}
          </>
        )}
      </span>

      {/* What the artist captured at this sitting, under the sitting it came from. */}
      {photos.length > 0 ? (
        <div className="col-start-2 col-span-2">
          <PhotoGallery
            photos={photos.map(galleryItem)}
            size="thumb"
            density="strip"
            maxVisible={5}
            testid={`client-history-visit-photos-${booking.id}`}
          />
        </div>
      ) : null}
    </li>
  );
}
